import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  IconButton
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  apiKeyItem: {
    display: "flex",
    flexDirection: "column",
    width: "100%"
  },
  apiKeyInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5)
  },
  addButton: {
    marginTop: theme.spacing(1)
  }
}));

const TextMeBotApiKeySelector = ({ value, onChange, disabled }) => {
  const classes = useStyles();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: "", apiKey: "" });
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/textmebot/apikeys");
      setApiKeys(data);
      
      // Si hay APIKeys y no hay una seleccionada, seleccionar la primera activa
      if (data.length > 0 && !value) {
        const activeKey = data.find(k => k.isActive && k.status === 'connected');
        if (activeKey) {
          onChange(activeKey.id);
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddApiKey = async () => {
    if (!newApiKey.name || !newApiKey.apiKey) {
      toast.error("Por favor complete todos los campos");
      return;
    }

    setVerifying(true);
    try {
      const { data } = await api.post("/textmebot/apikeys", newApiKey);
      
      if (data.status === 'connected') {
        toast.success(`APIKey conectada correctamente. Número: ${data.phoneNumber}`);
        loadApiKeys();
        setShowAddDialog(false);
        setNewApiKey({ name: "", apiKey: "" });
        onChange(data.id);
      } else {
        toast.error("No se pudo conectar con la APIKey");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyApiKey = async (id) => {
    try {
      const { data } = await api.post(`/textmebot/apikeys/${id}/verify`);
      
      if (data.status === 'connected') {
        toast.success(`Conexión verificada. Número: ${data.phoneNumber}`);
      } else {
        toast.warning("APIKey desconectada");
      }
      
      loadApiKeys();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteApiKey = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta APIKey?")) {
      return;
    }

    try {
      await api.delete(`/textmebot/apikeys/${id}`);
      toast.success("APIKey eliminada exitosamente");
      loadApiKeys();
      
      if (value === id) {
        onChange("");
      }
    } catch (err) {
      toastError(err);
    }
  };

  const selectedApiKey = apiKeys.find(k => k.id === value);

  return (
    <>
      <FormControl fullWidth required disabled={disabled || loading}>
        <InputLabel>APIKey de TextMeBot</InputLabel>
        <Select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {apiKeys.map((apiKey) => (
            <MenuItem key={apiKey.id} value={apiKey.id}>
              <div className={classes.apiKeyItem}>
                <Typography>{apiKey.name}</Typography>
                <div className={classes.apiKeyInfo}>
                  <Typography variant="caption" color="textSecondary">
                    {apiKey.phoneNumber}
                  </Typography>
                  <Chip
                    size="small"
                    label={apiKey.status === 'connected' ? 'Conectado' : 'Desconectado'}
                    color={apiKey.status === 'connected' ? 'primary' : 'default'}
                  />
                </div>
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedApiKey && (
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <Chip
            label={`${selectedApiKey.phoneNumber} - ${selectedApiKey.status === 'connected' ? '✓ Conectado' : '✗ Desconectado'}`}
            color={selectedApiKey.status === 'connected' ? 'primary' : 'default'}
            variant="outlined"
          />
          <IconButton
            size="small"
            onClick={() => handleVerifyApiKey(selectedApiKey.id)}
            title="Verificar conexión"
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteApiKey(selectedApiKey.id)}
            title="Eliminar APIKey"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      )}

      <Button
        className={classes.addButton}
        startIcon={<AddIcon />}
        onClick={() => setShowAddDialog(true)}
        disabled={disabled}
        variant="outlined"
        size="small"
      >
        Agregar Nueva APIKey
      </Button>

      {/* Dialog para agregar nueva APIKey */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar APIKey de TextMeBot</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            value={newApiKey.name}
            onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Ej: APIKey Principal, APIKey Secundaria"
          />
          <TextField
            label="APIKey"
            value={newApiKey.apiKey}
            onChange={(e) => setNewApiKey({ ...newApiKey, apiKey: e.target.value })}
            fullWidth
            margin="normal"
            helperText="Ingrese su APIKey de TextMeBot"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleAddApiKey}
            color="primary"
            disabled={verifying}
            variant="contained"
          >
            {verifying ? "Verificando..." : "Agregar y Verificar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TextMeBotApiKeySelector;
